/**
 * Image Service
 * Handles image URL construction with base URL
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.essentick.com/api';
const BASE_URL = API_BASE_URL.replace('/api', '');
const CDN_URL = process.env.REACT_APP_CDN_URL || process.env.REACT_APP_CDN_DOMAIN || 'https://cdn.essentick.com';

/**
 * Constructs full image URL from relative path
 * @param {string} imagePath - Relative image path (e.g., /uploads/products/filename.jpg) or full URL
 * @returns {string|null} - Full image URL or null if imagePath is empty
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // Convert to string and trim
  const trimmedPath = String(imagePath).trim();
  
  // Fix malformed URLs that have localhost in front of https
  if (trimmedPath.includes('localhost') && trimmedPath.includes('https://')) {
    const fixedPath = trimmedPath.replace(/^https?:\/\/[^/]+\//, '').replace(/^\/+/, '');
    return fixedPath;
  }
  
  // Check if it's already a full URL (more robust check)
  if (trimmedPath.match(/^https?:\/\//i)) {
    return trimmedPath;
  }

  // Use CDN URL if configured
  if (CDN_URL) {
    const cdnBase = CDN_URL.endsWith('/') ? CDN_URL.slice(0, -1) : CDN_URL;
    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
    return `${cdnBase}${normalizedPath}`;
  }

  // Fallback to API base URL
  const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  const fullUrl = `${BASE_URL}${normalizedPath}`;

  return fullUrl;
};

/**
 * Gets the base URL for images
 * @returns {string} - Base URL without /api
 */
export const getBaseUrl = () => {
  return BASE_URL;
};

/**
 * Gets primary image from product images array
 * @param {Array} images - Array of product images
 * @returns {Object|null} - Primary image object or first image or null
 */
export const getPrimaryImage = (images) => {
  if (!images || images.length === 0) return null;
  return images.find(img => img.is_primary) || images[0];
};

/**
 * Gets image URL from product images array
 * @param {Array} images - Array of product images
 * @returns {string|null} - Full image URL or null
 */
export const getProductImageUrl = (images) => {
  const primaryImage = getPrimaryImage(images);
  if (!primaryImage?.image_url) return null;
  return getImageUrl(primaryImage.image_url);
};

export default {
  getImageUrl,
  getBaseUrl,
  getPrimaryImage,
  getProductImageUrl,
};

